import { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import Daysfilterdropdown from "../components/Common/Daysfilterdropdown";
import useFleetStore from "../utils/store";
import HistoricalRuntime from "../components/Charts/HistoricalRuntime";
import RuntimeBarChart from "../components/Charts/EnergyUsageHorizontalBar";
import {
  deleteGlobalElectricRate,
  fetchElectricRateOptions,
  fetchEnergyUsage,
  fetchFleetElectricRate,
  fetchTopEnergyUsersRequest,
  saveFleetElectricRate,
  saveGlobalElectricRate,
} from "../lib/api";
import LoadingModal from "../components/Common/LoadingModal";
import SummaryCard from "../components/Common/SummaryCard";
import CurrentUserContext from "../contexts/CurrentUserContext";
import {
  convertKWhToPreferredUnit,
  formatEnergyValue,
  formatEnergyWithUnit,
  getEnergyUnitLabel,
} from "../utils/energyUnit";
import {
  getEffectiveElectricRate,
  getUniqueElectricRateOptions,
} from "../utils/electricRate";
import Input from "../components/Common/Input";
import Checkbox from "../components/Common/Checkbox";
import Button from "../components/Common/Button";
import ToastMessage from "../components/Common/ToastMessage";
import Pagination from "../components/Common/Pagination";
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";
import {
  COOLING_LAST_YEAR_LABEL,
  HEATING_LAST_YEAR_LABEL,
  buildComparisonChartData,
  formatMonthDay,
  getUtcDayKey,
} from "../utils/lastYearComparison";

const escapeCsvValue = (value) => {
  const stringValue = String(value ?? "");

  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const INITIAL_ELECTRIC_RATE_FORM = {
  region: "",
  buildingType: "",
  manuallyEnteredRate: "",
  mode: null,
};
const MAX_MANUAL_ELECTRIC_RATE = 1000;
const DEFAULT_TOP_USERS_PAGE_SIZE = 10;

const getElectricRateFormState = (electricRate) => {
  if (!electricRate) {
    return INITIAL_ELECTRIC_RATE_FORM;
  }

  if (electricRate?.manuallyEnteredRate !== null && electricRate?.manuallyEnteredRate !== undefined) {
    return {
      region: "",
      buildingType: "",
      manuallyEnteredRate: `${electricRate.manuallyEnteredRate}`,
      mode: "manual",
    };
  }

  if (electricRate?.calculatedRate) {
    return {
      region: electricRate.calculatedRate.region || "",
      buildingType: electricRate.calculatedRate.buildingType || "",
      manuallyEnteredRate: "",
      mode: "calculated",
    };
  }

  return INITIAL_ELECTRIC_RATE_FORM;
};

const getFormattedRate = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : "0.00";
};

const formatCurrencyValue = (value) => {
  const numericValue = Number(value) || 0;
  return numericValue.toFixed(2);
};

const sanitizeManualElectricRateInput = (value) => {
  if (value === "") {
    return "";
  }

  if (!/^\d*\.?\d*$/.test(value)) {
    return null;
  }

  const [wholeNumberPart = "", decimalPart = ""] = value.split(".");

  if (!value.includes(".")) {
    return wholeNumberPart;
  }

  return `${wholeNumberPart}.${decimalPart.slice(0, 2)}`;
};

const COMPARE_LAST_YEAR_STORAGE_KEY = "energy-compare-last-year";
const LAST_YEAR_FETCH_FAILED_MESSAGE =
  "Could not load the same-time-last-year comparison. Uncheck \"Compare with same time last year\" to see current data.";

const readStoredCompareLastYear = () => {
  try {
    return window.localStorage.getItem(COMPARE_LAST_YEAR_STORAGE_KEY) === "true";
  } catch (error) {
    return false;
  }
};

const storeCompareLastYear = (compareLastYear) => {
  try {
    window.localStorage.setItem(COMPARE_LAST_YEAR_STORAGE_KEY, String(compareLastYear));
  } catch (error) {
    return;
  }
};

const getElectricRateEstimatorVisibilityStorageKey = (fleetUnid) =>
  `energy-rate-estimator-visible:${fleetUnid}`;

const areElectricRateFormsEqual = (left, right) => {
  const leftMode = left?.mode || null;
  const rightMode = right?.mode || null;

  if (leftMode !== rightMode) {
    return false;
  }

  if (leftMode === "manual") {
    const leftRate = left?.manuallyEnteredRate === "" ? "" : Number(left?.manuallyEnteredRate);
    const rightRate = right?.manuallyEnteredRate === "" ? "" : Number(right?.manuallyEnteredRate);

    return leftRate === rightRate;
  }

  if (leftMode === "calculated") {
    return (
      (left?.region || "") === (right?.region || "") &&
      (left?.buildingType || "") === (right?.buildingType || "")
    );
  }

  return true;
};

const Energy = () => {
  const isAccessRestricted = useIsWriteRestrictedUser();
  const [selectedOption, setSelectedOption] = useState("Last 24 Hrs");
  const selectedFleet = useFleetStore((state) => state.selectedFleet);
  const { currentUser } = useContext(CurrentUserContext);
  const energyUnit = currentUser?.energyUnit;
  const energyUnitLabel = getEnergyUnitLabel(energyUnit);
  const [energyUsage, setEnergyUsage] = useState();
  const [topRunTimeUnits, setTopRunTimeUnits] = useState();
  const [compareLastYear, setCompareLastYear] = useState(readStoredCompareLastYear);
  const [lastYearTopRunTimeUnits, setLastYearTopRunTimeUnits] = useState([]);
  const [lastYearTopUsersTotalCount, setLastYearTopUsersTotalCount] = useState(0);
  const [topUsersTotalCount, setTopUsersTotalCount] = useState(0);
  const [topUsersCurrentPage, setTopUsersCurrentPage] = useState(0);
  const [electricRateOptions, setElectricRateOptions] = useState([]);
  const [configuredElectricRate, setConfiguredElectricRate] = useState(null);
  const [electricRateForm, setElectricRateForm] = useState(INITIAL_ELECTRIC_RATE_FORM);
  const [applyToAllZones, setApplyToAllZones] = useState(false);
  const [hadGlobalElectricRate, setHadGlobalElectricRate] = useState(false);
  const [isElectricRateFormExpanded, setIsElectricRateFormExpanded] = useState(true);
  const [isFetchingEnergyUsage, setIsFetchingEnergyUsage] = useState(false);
  const [isFetchingTopRunTimeUnits, setIsFetchingTopRunTimeUnits] = useState(false);
  const [isFetchingElectricRateData, setIsFetchingElectricRateData] = useState(false);
  const [isSavingElectricRate, setIsSavingElectricRate] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const latestTopUsersRequestRef = useRef(0);
  const latestEnergyUsageRequestRef = useRef(0);
  const rangeMap = {
    "Last 24 Hrs": "24h",
    "Last 7 Days": "7d",
    "Last 30 Days": "30d",
  };
  const topUsersTimePeriod = rangeMap[selectedOption] || "30d";
  const energyUsageRangeMap = {
    "Last 24 Hrs": "timePeriod24h",
    "Last 7 Days": "timePeriod7d",
    "Last 30 Days": "timePeriod30d",
  };
  const selectedRange = energyUsageRangeMap[selectedOption] || "timePeriod30d";

  const handleTopUsersTimeRangeChange = useCallback((nextOption) => {
    setTopUsersCurrentPage(0);
    setSelectedOption(nextOption);
  }, []);

  const handleCompareLastYearChange = useCallback((checked) => {
    setTopUsersCurrentPage(0);
    setCompareLastYear(Boolean(checked));
    storeCompareLastYear(Boolean(checked));
  }, []);

  const persistElectricRateFormExpandedState = useCallback(
    (isExpanded) => {
      if (!selectedFleet?.unid || typeof window === "undefined") {
        return;
      }

      window.localStorage.setItem(
        getElectricRateEstimatorVisibilityStorageKey(selectedFleet.unid),
        JSON.stringify(isExpanded)
      );
    },
    [selectedFleet?.unid]
  );

  const setElectricRateFormExpandedWithPersistence = useCallback(
    (isExpanded) => {
      setIsElectricRateFormExpanded(isExpanded);
      persistElectricRateFormExpandedState(isExpanded);
    },
    [persistElectricRateFormExpandedState]
  );

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
  }, []);

  const GetEnergyUsage = useCallback(async () => {
    const requestId = latestEnergyUsageRequestRef.current + 1;
    latestEnergyUsageRequestRef.current = requestId;

    setIsFetchingEnergyUsage(true);
    try {
      const response = await fetchEnergyUsage({
        unid: selectedFleet?.unid,
        includeLastYear: compareLastYear,
      });
      if (latestEnergyUsageRequestRef.current === requestId) {
        setEnergyUsage(response?.data);
      }
    } catch (error) {
      if (latestEnergyUsageRequestRef.current === requestId) {
        console.error("Error fetching energy usage:", error);
        if (compareLastYear) {
          showToast(LAST_YEAR_FETCH_FAILED_MESSAGE, "failed");
        }
      }
    } finally {
      if (latestEnergyUsageRequestRef.current === requestId) {
        setIsFetchingEnergyUsage(false);
      }
    }
  }, [selectedFleet?.unid, compareLastYear, showToast]);

  const GetTopRunTimeUnits = useCallback(async () => {
    if (!selectedFleet?.unid) {
      return;
    }

    const requestId = latestTopUsersRequestRef.current + 1;
    latestTopUsersRequestRef.current = requestId;

    setIsFetchingTopRunTimeUnits(true);
    try {
      const response = await fetchTopEnergyUsersRequest({
        unid: selectedFleet.unid,
        timePeriod: topUsersTimePeriod,
        pageNumber: topUsersCurrentPage + 1,
        pageSize: DEFAULT_TOP_USERS_PAGE_SIZE,
        includeLastYear: compareLastYear,
      });

      if (latestTopUsersRequestRef.current !== requestId) {
        return;
      }

      setTopRunTimeUnits(response?.data?.data?.topUsers || []);
      setTopUsersTotalCount(response?.data?.data?.count || 0);
      setLastYearTopRunTimeUnits(response?.data?.data?.lastYear?.topUsers || []);
      setLastYearTopUsersTotalCount(response?.data?.data?.lastYear?.count || 0);
    } catch (error) {
      if (latestTopUsersRequestRef.current !== requestId) {
        return;
      }

      console.error("Error fetching top energy users:", error);
      if (compareLastYear) {
        showToast(LAST_YEAR_FETCH_FAILED_MESSAGE, "failed");
      }
      setTopRunTimeUnits([]);
      setTopUsersTotalCount(0);
      setLastYearTopRunTimeUnits([]);
      setLastYearTopUsersTotalCount(0);
    } finally {
      if (latestTopUsersRequestRef.current === requestId) {
        setIsFetchingTopRunTimeUnits(false);
      }
    }
  }, [selectedFleet?.unid, topUsersCurrentPage, topUsersTimePeriod, compareLastYear, showToast]);

  const loadElectricRateData = useCallback(async () => {
    if (!selectedFleet?.unid) {
      return;
    }

    setIsFetchingElectricRateData(true);
    try {
      const [electricRateResponse, electricRateOptionsResponse] = await Promise.all([
        fetchFleetElectricRate({ unid: selectedFleet.unid }),
        fetchElectricRateOptions(),
      ]);

      const options = getUniqueElectricRateOptions(
        electricRateOptionsResponse?.data?.data?.options || []
      );
      const electricRate = electricRateResponse?.data?.data?.electricRate || null;

      setElectricRateOptions(options);
      setConfiguredElectricRate(electricRate);
      setElectricRateForm(getElectricRateFormState(electricRate));
      setApplyToAllZones(Boolean(electricRate?.appliesToAllZones));
      setHadGlobalElectricRate(Boolean(electricRate?.appliesToAllZones));
    } catch (error) {
      console.error("Error fetching electric rate data:", error);
      setElectricRateOptions([]);
      setConfiguredElectricRate(null);
      setElectricRateForm(INITIAL_ELECTRIC_RATE_FORM);
      setApplyToAllZones(false);
      setHadGlobalElectricRate(false);
      showToast(error.message || "Failed to fetch electric rate data.", "failed");
    } finally {
      setIsFetchingElectricRateData(false);
    }
  }, [selectedFleet?.unid, showToast]);

  useEffect(() => {
    if (selectedFleet?.unid) {
      GetEnergyUsage();
    }
  }, [selectedFleet, GetEnergyUsage]);

  useEffect(() => {
    if (selectedFleet?.unid) {
      GetTopRunTimeUnits();
    }
  }, [selectedFleet?.unid, GetTopRunTimeUnits]);

  useEffect(() => {
    setTopUsersCurrentPage(0);
  }, [selectedFleet?.unid]);

  useEffect(() => {
    if (selectedFleet?.unid) {
      loadElectricRateData();
    }
  }, [selectedFleet, loadElectricRateData]);

  useEffect(() => {
    if (!selectedFleet?.unid || typeof window === "undefined") {
      return;
    }

    const storedVisibility = window.localStorage.getItem(
      getElectricRateEstimatorVisibilityStorageKey(selectedFleet.unid)
    );

    if (storedVisibility === null) {
      setIsElectricRateFormExpanded(true);
      return;
    }

    try {
      setIsElectricRateFormExpanded(JSON.parse(storedVisibility));
    } catch {
      setIsElectricRateFormExpanded(true);
    }
  }, [selectedFleet?.unid]);

  const processChartData = (data = []) =>
    data.map((item) => {
      const dateObj = new Date(item.timestamp);
      return {
        date: formatMonthDay(dateObj),
        dayKey: getUtcDayKey(dateObj),
        totalKWh: Number(item.totalKWh || 0),
        energyUsage: Number(
          convertKWhToPreferredUnit(item.totalKWh, energyUnit).toFixed(2)
        ),
      };
    });

  const groupByDate = (data = []) => {
    const result = {};
    data.forEach((item) => {
      const date = item.date;
      const energyUsage = parseFloat(item.energyUsage) || 0;
      const energyCost = parseFloat(item.energyCost) || 0;

      if (!result[date]) {
        result[date] = {
          date,
          dayKey: item.dayKey,
          energyUsage: 0,
          energyCost: 0,
        };
      }

      result[date].energyUsage += energyUsage;
      result[date].energyCost += energyCost;
    });
    return Object.values(result).map((item) => ({
      date: item.date,
      dayKey: item.dayKey,
      energyUsage: parseFloat(item.energyUsage.toFixed(2)),
      energyCost: parseFloat(item.energyCost.toFixed(2)),
    }));
  };

  const getPercentChangeDisplay = (value) => {
    if (value === null || value === undefined) {
      return "N/A";
    }

    const numericValue = Number(value) || 0;
    return `${numericValue > 0 ? "+" : ""}${numericValue}%`;
  };

  const getPercentChangeTextClass = (value) => {
    if (value === null || value === undefined) {
      return "text-dark-grey dark:text-white/70";
    }

    if (value <= 0) {
      return "text-[#00A653]";
    }

    return "text-[#D80B2D]";
  };

  const getPercentChangeBarClass = (value) => {
    if (value === null || value === undefined) {
      return "bg-[#BDBDBD] dark:bg-white/30";
    }

    if (value <= 0) {
      return "bg-[#09F]";
    }

    return "bg-[#D80B2D]";
  };

  const handleCalculatedRateChange = (field, value) => {
    setElectricRateForm((prev) => {
      if (field === "region") {
        const nextRegion = value;
        const buildingStillValid = electricRateOptions.some(
          (option) =>
            option.region === nextRegion && option.buildingType === prev.buildingType
        );

        return {
          ...prev,
          region: nextRegion,
          buildingType: buildingStillValid ? prev.buildingType : "",
          mode: "calculated",
        };
      }

      return {
        ...prev,
        [field]: value,
        mode: "calculated",
      };
    });
  };

  const handleManualRateChange = (event) => {
    const nextValue = sanitizeManualElectricRateInput(event.target.value);
    if (nextValue === null) {
      return;
    }

    setElectricRateForm((prev) => ({
      ...prev,
      manuallyEnteredRate: nextValue,
      mode: "manual",
    }));
  };

  const uniqueElectricRateOptions = getUniqueElectricRateOptions(electricRateOptions);
  const regionOptions = [...new Set(uniqueElectricRateOptions.map((option) => option.region))];
  const buildingOptions = [
    ...new Set(
      uniqueElectricRateOptions
        .filter(
          (option) =>
            !electricRateForm.region || option.region === electricRateForm.region
        )
        .map((option) => option.buildingType)
    ),
  ];
  const selectedCalculatedRateOption = uniqueElectricRateOptions.find(
    (option) =>
      option.region === electricRateForm.region &&
      option.buildingType === electricRateForm.buildingType
  );
  const selectedCalculatedRate = selectedCalculatedRateOption?.rate;
  const hasCalculatedSelection = Boolean(
    electricRateForm.region && electricRateForm.buildingType
  );
  const hasManualSelection = electricRateForm.manuallyEnteredRate !== "";

  const buildElectricRatePayload = () => {
    if (electricRateForm.mode === "manual" && hasManualSelection) {
      const manuallyEnteredRate = Number(electricRateForm.manuallyEnteredRate);
      if (!Number.isFinite(manuallyEnteredRate)) {
        return null;
      }

      return {
        manuallyEnteredRate,
      };
    }

    if (hasCalculatedSelection) {
      return {
        calculatedRate: {
          region: electricRateForm.region,
          buildingType: electricRateForm.buildingType,
        },
      };
    }

    return null;
  };

  const savedElectricRateForm = getElectricRateFormState(configuredElectricRate);
  const activeElectricRateMode = savedElectricRateForm.mode;
  const hasElectricRateFormChanges = !areElectricRateFormsEqual(
    electricRateForm,
    savedElectricRateForm
  );
  const hasApplyToAllZonesChanged =
    applyToAllZones !== Boolean(configuredElectricRate?.appliesToAllZones);
  const hasSavableElectricRateChanges =
    Boolean(buildElectricRatePayload()) &&
    (hasElectricRateFormChanges || hasApplyToAllZonesChanged);
  const isElectricRateSaveDisabled =
    isAccessRestricted || isSavingElectricRate || !hasSavableElectricRateChanges;

  const handleSaveElectricRate = async () => {
    if (isAccessRestricted) return;

    if (
      electricRateForm.mode === "manual" &&
      Number(electricRateForm.manuallyEnteredRate) > MAX_MANUAL_ELECTRIC_RATE
    ) {
      showToast(
        `Electric rate cannot exceed ${MAX_MANUAL_ELECTRIC_RATE} cents/kWh.`,
        "failed"
      );
      return;
    }

    const payload = buildElectricRatePayload();

    if (!payload) {
      showToast("Please select a calculated rate or enter a manual rate.", "failed");
      return;
    }

    setIsSavingElectricRate(true);
    try {
      const response = applyToAllZones
        ? await saveGlobalElectricRate(payload)
        : await saveFleetElectricRate({
            unid: selectedFleet.unid,
            electricRate: payload,
          });

      if (!applyToAllZones && hadGlobalElectricRate) {
        await deleteGlobalElectricRate();
      }

      const savedElectricRate = response?.data?.data?.electricRate || null;
      setConfiguredElectricRate(savedElectricRate);
      setElectricRateForm(getElectricRateFormState(savedElectricRate));
      setApplyToAllZones(Boolean(savedElectricRate?.appliesToAllZones));
      setHadGlobalElectricRate(Boolean(savedElectricRate?.appliesToAllZones));
      setElectricRateFormExpandedWithPersistence(false);
      showToast("Electric rate saved successfully.", "success");
    } catch (error) {
      console.error("Error saving electric rate:", error);
      showToast(error.message || "Failed to save electric rate.", "failed");
    } finally {
      setIsSavingElectricRate(false);
    }
  };

  const formattedData = processChartData(
    energyUsage?.data?.[selectedRange] || []
  );
  const effectiveElectricRate = getEffectiveElectricRate(
    configuredElectricRate,
    uniqueElectricRateOptions
  );
  const hasConfiguredElectricRate = Number.isFinite(effectiveElectricRate);
  const groupedData = groupByDate(
    formattedData.map((item) => ({
      ...item,
      energyCost: hasConfiguredElectricRate
        ? Number((item.totalKWh * effectiveElectricRate).toFixed(2))
        : undefined,
    }))
  );
  const lastYearEnergyData = compareLastYear
    ? energyUsage?.data?.lastYear?.[selectedRange] || []
    : [];
  const hasLastYearEnergyData = lastYearEnergyData.length > 0;
  const chartData = hasLastYearEnergyData
    ? buildComparisonChartData({
        currentDays: groupedData,
        lastYearEnergyData,
        toDisplayUsage: (kWh) =>
          Number(convertKWhToPreferredUnit(kWh, energyUnit).toFixed(2)),
        toCost: hasConfiguredElectricRate
          ? (kWh) => Number(((kWh || 0) * effectiveElectricRate).toFixed(2))
          : null,
      })
    : groupedData;
  const lastYearTotalEnergyKWh = lastYearEnergyData.reduce(
    (sum, item) => sum + (item.totalKWh || 0),
    0
  );
  const lastYearAvgPerUnitEnergyKWh = lastYearEnergyData.reduce(
    (sum, item) => sum + (item.avgPerUnitKWh || 0),
    0
  );
  const totalEnergyUsageKWh = (energyUsage?.data?.[selectedRange] || []).reduce(
    (sum, item) => sum + (item.totalKWh || 0),
    0
  );
  const avgPerUnitEnergyKWh = (energyUsage?.data?.[selectedRange] || []).reduce(
    (sum, item) => sum + (item.avgPerUnitKWh || 0),
    0
  );
  const getPercentChangeFromLastYear = (current, lastYear) =>
    lastYear > 0 ? Number((((current - lastYear) / lastYear) * 100).toFixed(1)) : null;
  const getLastYearSubLabel = (current, lastYear, testIdPrefix) => {
    if (!hasLastYearEnergyData) {
      return `(${selectedOption})`;
    }
    const percentChange = getPercentChangeFromLastYear(current, lastYear);
    const lastYearDisplay = hasConfiguredElectricRate
      ? `${formatEnergyWithUnit(lastYear, energyUnit)} ($${formatCurrencyValue(lastYear * effectiveElectricRate)})`
      : formatEnergyWithUnit(lastYear, energyUnit);
    return (
      <>
        {`(${selectedOption})`}
        <span className="block" data-testid={`${testIdPrefix}-last-year`}>
          Last year: {lastYearDisplay}
        </span>
        <span
          className={`block font-medium ${getPercentChangeTextClass(percentChange)}`}
          data-testid={`${testIdPrefix}-last-year-change`}
        >
          {getPercentChangeDisplay(percentChange)} vs last year
        </span>
      </>
    );
  };
  const totalEnergyUsage = formatEnergyValue(totalEnergyUsageKWh, energyUnit);
  const avgPerUnitEnergy = formatEnergyValue(avgPerUnitEnergyKWh, energyUnit);
  const totalEnergyUsageCost = hasConfiguredElectricRate
    ? `$${formatCurrencyValue(totalEnergyUsageKWh * effectiveElectricRate)}`
    : null;
  const avgPerUnitEnergyCost = hasConfiguredElectricRate
    ? `$${formatCurrencyValue(avgPerUnitEnergyKWh * effectiveElectricRate)}`
    : null;
  const totalEnergyUsageDisplay = totalEnergyUsageCost
    ? `${totalEnergyUsage} ${energyUnitLabel} (${totalEnergyUsageCost})`
    : `${totalEnergyUsage} ${energyUnitLabel}`;
  const avgPerUnitEnergyDisplay = avgPerUnitEnergyCost
    ? `${avgPerUnitEnergy} ${energyUnitLabel} (${avgPerUnitEnergyCost})`
    : `${avgPerUnitEnergy} ${energyUnitLabel}`;
  const selectedEnergyData = energyUsage?.data?.[selectedRange] || [];
  const hasHeatingCoolingData = selectedEnergyData.length > 0 || hasLastYearEnergyData;
  const heatingSum = selectedEnergyData.reduce(
    (sum, item) => sum + (item.heatingKWh || 0),
    0
  );
  const coolingSum = selectedEnergyData.reduce(
    (sum, item) => sum + (item.coolingKWh || 0),
    0
  );
  const lastYearHeatingSum = lastYearEnergyData.reduce(
    (sum, item) => sum + (item.heatingKWh || 0),
    0
  );
  const lastYearCoolingSum = lastYearEnergyData.reduce(
    (sum, item) => sum + (item.coolingKWh || 0),
    0
  );
  const getEnergyChartLabel = useCallback((kWh) => (
    `${formatEnergyWithUnit(kWh, energyUnit)}${
      hasConfiguredElectricRate
        ? ` ($${formatCurrencyValue(kWh * effectiveElectricRate)})`
        : ""
    }`
  ), [effectiveElectricRate, energyUnit, hasConfiguredElectricRate]);
  const getEnergyChartCost = useCallback((kWh) => (
    hasConfiguredElectricRate
      ? Number((kWh * effectiveElectricRate).toFixed(2))
      : null
  ), [effectiveElectricRate, hasConfiguredElectricRate]);
  const runtimeChartData = useMemo(() => {
    const currentRows = [
      {
        label: "Heating",
        category: "Heating",
        value: Number(convertKWhToPreferredUnit(heatingSum, energyUnit).toFixed(2)),
        cost: getEnergyChartCost(heatingSum),
        valueLabel: getEnergyChartLabel(heatingSum),
      },
      {
        label: "Cooling",
        category: "Cooling",
        cooling: Number(convertKWhToPreferredUnit(coolingSum, energyUnit).toFixed(2)),
        cost: getEnergyChartCost(coolingSum),
        coolingLabel: getEnergyChartLabel(coolingSum),
      },
    ];
    if (!hasLastYearEnergyData) {
      return currentRows;
    }
    const [heatingRow, coolingRow] = currentRows;
    return [
      heatingRow,
      {
        label: HEATING_LAST_YEAR_LABEL,
        category: "Heating",
        isLastYear: true,
        lastYearValue: Number(convertKWhToPreferredUnit(lastYearHeatingSum, energyUnit).toFixed(2)),
        cost: getEnergyChartCost(lastYearHeatingSum),
        lastYearValueLabel: getEnergyChartLabel(lastYearHeatingSum),
      },
      coolingRow,
      {
        label: COOLING_LAST_YEAR_LABEL,
        category: "Cooling",
        isLastYear: true,
        lastYearCooling: Number(convertKWhToPreferredUnit(lastYearCoolingSum, energyUnit).toFixed(2)),
        cost: getEnergyChartCost(lastYearCoolingSum),
        lastYearCoolingLabel: getEnergyChartLabel(lastYearCoolingSum),
      },
    ];
  }, [
    coolingSum,
    energyUnit,
    getEnergyChartCost,
    getEnergyChartLabel,
    hasLastYearEnergyData,
    heatingSum,
    lastYearCoolingSum,
    lastYearHeatingSum,
  ]);
  const downloadCsvFile = useCallback((rows, fileNameSuffix) => {
    if (!rows.length || typeof window === "undefined") {
      return;
    }

    const csvContent = rows
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const fileNameFleetPart = (selectedFleet?.name || "fleet")
      .trim()
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();
    const fileNameSuffixPart = fileNameSuffix
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "");
    const downloadLink = document.createElement("a");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const objectUrl = window.URL.createObjectURL(blob);

    downloadLink.href = objectUrl;
    downloadLink.download = `${fileNameFleetPart || "fleet"}-${fileNameSuffixPart || "report"}.csv`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(objectUrl);
  }, [selectedFleet?.name]);

  const handleDownloadEnergyUsageOverTimeCsv = useCallback(() => {
    if (!chartData.length) {
      return;
    }

    const headers = ["Date", `Energy Usage (${energyUnitLabel})`];
    if (hasConfiguredElectricRate) {
      headers.push("Energy Cost ($)");
    }
    if (hasLastYearEnergyData) {
      headers.push("Last Year Date", `Last Year Energy Usage (${energyUnitLabel})`);
      if (hasConfiguredElectricRate) {
        headers.push("Last Year Energy Cost ($)");
      }
    }

    downloadCsvFile([
      headers,
      ...chartData.map((item) => {
        const row = [item.date, item.energyUsage ?? ""];
        if (hasConfiguredElectricRate) {
          row.push(item.energyCost ?? "");
        }
        if (hasLastYearEnergyData) {
          row.push(item.lastYearDate ?? "", item.lastYearEnergyUsage ?? "");
          if (hasConfiguredElectricRate) {
            row.push(item.lastYearEnergyCost ?? "");
          }
        }
        return row;
      }),
    ], `energy-usage-over-time-${selectedOption}${hasLastYearEnergyData ? "-with-last-year" : ""}`);
  }, [
    chartData,
    downloadCsvFile,
    energyUnitLabel,
    hasConfiguredElectricRate,
    hasLastYearEnergyData,
    selectedOption,
  ]);

  const handleDownloadHeatingCoolingEnergyUsageCsv = useCallback(() => {
    if (!hasHeatingCoolingData) {
      return;
    }

    const lastYearValueByCategory = Object.fromEntries(
      runtimeChartData
        .filter((item) => item.isLastYear)
        .map((item) => [
          item.category,
          item.lastYearValue ?? item.lastYearCooling ?? 0,
        ])
    );
    const headers = ["Category", `Energy Usage (${energyUnitLabel})`];
    if (hasLastYearEnergyData) {
      headers.push(`Last Year Energy Usage (${energyUnitLabel})`);
    }

    downloadCsvFile([
      headers,
      ...runtimeChartData
        .filter((item) => !item.isLastYear)
        .map((item) => {
          const row = [item.label, item.value ?? item.cooling ?? 0];
          if (hasLastYearEnergyData) {
            row.push(lastYearValueByCategory[item.category] ?? 0);
          }
          return row;
        }),
    ], `heating-cooling-energy-usage-${selectedOption}${hasLastYearEnergyData ? "-with-last-year" : ""}`);
  }, [
    downloadCsvFile,
    energyUnitLabel,
    hasLastYearEnergyData,
    hasHeatingCoolingData,
    runtimeChartData,
    selectedOption,
  ]);

  const topUnitsByRange = topRunTimeUnits || [];
  const lastYearTopUnits = compareLastYear ? lastYearTopRunTimeUnits : [];

  const topUsersPageCount = Math.ceil(
    Math.max(topUsersTotalCount, compareLastYear ? lastYearTopUsersTotalCount : 0) /
      DEFAULT_TOP_USERS_PAGE_SIZE
  );

  const renderTopUserRow = (unit, index, testIdPrefix) => {
    const rowId = unit?.unit?.serialNumber || index;
    return (
      <div
        key={rowId}
        className="flex flex-col gap-4 border-b dark:border-b-darkThemeBorder py-5 px-4 last:border-b-0 w-full"
        data-testid={`${testIdPrefix}-row-${rowId}`}
      >
        <div className="flex justify-between items-start basis-5/12 shrink grow">
          <div className="flex flex-col">
            <p title={unit?.unit?.metadata?.name} className="text-md sm:text-lg leading-4 sm:leading-6 tracking-[0.027px] font-medium text-dark-grey dark:text-white truncate max-w-40 sm:max-w-64 md:max-w-96 lg:max-w-60" data-testid={`${testIdPrefix}-name-${rowId}`}>
              {unit?.unit?.metadata?.name}
            </p>
            <p className="text-xs sm:text-sm leading-4 sm:leading-5 text-dark-grey dark:text-white" data-testid={`${testIdPrefix}-serial-${rowId}`}>
              {unit?.unit?.serialNumber}
            </p>
          </div>
          <div className="text-right flex flex-col basis-auto shrink-0">
            <p className="text-sm sm:text-base leading-4 sm:leading-5 text-dark-grey dark:text-white font-semibold" data-testid={`${testIdPrefix}-value-${rowId}`}>
              {hasConfiguredElectricRate
                ? `${formatEnergyWithUnit(unit?.totalKWh, energyUnit)} ($${formatCurrencyValue(
                    (unit?.totalKWh || 0) * effectiveElectricRate
                  )})`
                : formatEnergyWithUnit(unit?.totalKWh, energyUnit)}
            </p>
            <p
              className={`text-xs sm:text-sm font-medium leading-5 tracking-[0.021px] flex items-center justify-end gap-1 ${getPercentChangeTextClass(
                unit?.totalKWhPercentChangeSinceLastPeriod
              )}`}
              data-testid={`${testIdPrefix}-percent-change-${rowId}`}
            >
              <span>
                {getPercentChangeDisplay(unit?.totalKWhPercentChangeSinceLastPeriod)} vs {
                  {
                    "30d": "Last Month",
                    "7d": "Last Week",
                    "24h": "Last 24 Hrs",
                  }[topUsersTimePeriod] || ""
                }
              </span>
            </p>
          </div>
        </div>
        <div className="h-1 w-full bg-[#E0E0E0] dark:bg-[#F4F4E1]/25 rounded-full">
          <div
            className={`h-1 rounded-full ${getPercentChangeBarClass(
              unit?.totalKWhPercentChangeSinceLastPeriod
            )}`}
            style={{
              width: `${Math.min(
                100,
                Math.abs(unit?.totalKWhPercentChangeSinceLastPeriod || 0)
              )}%`,
            }}
          />
        </div>
      </div>
    );
  };

  const isLoading =
    isFetchingEnergyUsage ||
    isFetchingTopRunTimeUnits ||
    isFetchingElectricRateData ||
    isSavingElectricRate;
  const isElectricRateFormVisible =
    !hasConfiguredElectricRate || isElectricRateFormExpanded;

  return (
    <>
      {isLoading ? <LoadingModal /> : null}
      <div className="titleAndFilters mb-6" data-testid="energy-title-and-filters">
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <h2 className="mainTitle whitespace-nowrap text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="energy-title">
            Energy Management
          </h2>
          <div className="searchAndFilter flex flex-wrap items-center gap-4 w-full justify-end">
            <div className="flex items-center gap-3">
              <Daysfilterdropdown
                selectedOption={selectedOption}
                setSelectedOption={handleTopUsersTimeRangeChange}
                data-testid="energy-days-filter"
              />
            </div>
            <Checkbox
              label="Compare with same time last year"
              checked={compareLastYear}
              onChange={handleCompareLastYearChange}
              data-testid="energy-compare-last-year-checkbox"
            />
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-white dark:darkThemeGradiantBg shadow p-5 lg:p-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              {hasConfiguredElectricRate ? (
                <Button
                  type="button"
                  onClick={() =>
                    setElectricRateFormExpandedWithPersistence(!isElectricRateFormExpanded)
                  }
                  aria-expanded={isElectricRateFormExpanded}
                  aria-label={
                    isElectricRateFormExpanded
                      ? "Hide zone energy rate estimator"
                      : "Show zone energy rate estimator"
                  }
                  className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full border border-[#E1E5EA] text-[#D5671F] transition-transform hover:bg-[#FFF4EF] dark:border-darkThemeBorder dark:text-[#FF8C65]"
                  data-testid="energy-rate-estimator-toggle"
                >
                  <svg
                    aria-hidden="true"
                    className={`h-4 w-4 transition-transform ${
                      isElectricRateFormExpanded ? "rotate-0" : "-rotate-90"
                    }`}
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M3.5 6l4.5 4.5L12.5 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
              ) : null}
              <div>
                <h3 className="text-[#000000DE] dark:text-white text-xl font-semibold leading-6" data-testid="energy-rate-estimator-title">
                  Zone Energy Rate Estimator
                </h3>
                <p className="mt-1 text-dark-grey dark:text-white text-sm leading-5">
                  Set the average energy rate for your current zone
                </p>
              </div>
            </div>
            {isElectricRateFormVisible ? (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
                <Checkbox
                  label="Apply to all zones"
                  checked={applyToAllZones}
                  disabled={isAccessRestricted}
                  onChange={setApplyToAllZones}
                  data-testid="energy-rate-apply-all-checkbox"
                />
                <Button
                  onClick={handleSaveElectricRate}
                  disabled={isElectricRateSaveDisabled}
                  className={`h-11 min-w-[170px] rounded-lg px-6 text-base font-semibold text-white transition-colors ${
                    isElectricRateSaveDisabled
                      ? "cursor-not-allowed bg-[#FDC1AE]"
                      : "bg-theme hover:bg-[#ff7c51]"
                  }`}
                  data-testid="energy-rate-save-button"
                >
                  Save
                </Button>
              </div>
            ) : null}
          </div>

          {isElectricRateFormVisible ? (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] xl:gap-8">
              <div
                className={`flex flex-col gap-5 rounded-xl border-2 p-4 transition-colors xl:border-r xl:pr-8 ${
                  activeElectricRateMode === "calculated"
                    ? "border-theme bg-[#FFF4EF] dark:bg-darkThemeModalBg"
                    : "border-[#E1E5EA] bg-transparent dark:border-darkThemeBorder"
                }`}
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:flex-wrap">
                  <span className="text-dark-grey dark:text-white text-lg leading-6">
                    My zone is in
                  </span>
                  <div className="relative min-w-[220px]">
                    <select
                      name="region"
                      id="region"
                      value={electricRateForm.region}
                      onChange={(event) =>
                        handleCalculatedRateChange(event.target.name, event.target.value)
                      }
                      disabled={isAccessRestricted}
                      className="border border-[#E1E5EA] dark:border-darkThemeBorder bg-white dark:bg-darkThemeModalBg text-sm text-[#2F343A] dark:text-white rounded-lg h-12 pl-3 pr-10 appearance-none w-full focus:outline-none focus:ring-2 focus:ring-theme shadow-appShadow disabled:cursor-not-allowed disabled:opacity-70 dark:[&_option]:bg-white dark:[&_option]:text-[#14171F]"
                      data-testid="energy-rate-region-select"
                    >
                      <option value="">----</option>
                      {regionOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <svg
                      aria-hidden="true"
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2F343A] dark:text-white"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="text-dark-grey dark:text-white text-lg leading-6">
                    region and it is in a
                  </span>
                  <div className="relative min-w-[220px]">
                    <select
                      name="buildingType"
                      id="buildingType"
                      value={electricRateForm.buildingType}
                      onChange={(event) =>
                        handleCalculatedRateChange(event.target.name, event.target.value)
                      }
                      disabled={isAccessRestricted}
                      className="border border-[#E1E5EA] dark:border-darkThemeBorder bg-white dark:bg-darkThemeModalBg text-sm text-[#2F343A] dark:text-white rounded-lg h-12 pl-3 pr-10 appearance-none w-full focus:outline-none focus:ring-2 focus:ring-theme shadow-appShadow disabled:cursor-not-allowed disabled:opacity-70 dark:[&_option]:bg-white dark:[&_option]:text-[#14171F]"
                      data-testid="energy-rate-building-type-select"
                    >
                      <option value="">----</option>
                      {buildingOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <svg
                      aria-hidden="true"
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2F343A] dark:text-white"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="text-dark-grey dark:text-white text-lg leading-6">
                    Building.
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-dark-grey dark:text-white text-lg leading-6">
                  <span>Average Rates for your selection are</span>
                  <span className="min-w-16 font-semibold text-[#000000DE] dark:text-white" data-testid="energy-rate-calculated-value">
                    {getFormattedRate(selectedCalculatedRate)}
                  </span>
                  <span>¢/kWh</span>
                </div>
              </div>

              <div
                className={`flex flex-col justify-center gap-4 rounded-xl border-2 p-4 transition-colors ${
                  activeElectricRateMode === "manual"
                    ? "border-theme bg-[#FFF4EF] dark:bg-darkThemeModalBg"
                    : "border-[#E1E5EA] bg-transparent dark:border-darkThemeBorder"
                }`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <span className="text-dark-grey dark:text-white text-lg leading-6 lg:min-w-0">
                    Or fill in your average rate per kWh
                  </span>
                  <div className="flex shrink-0 items-center gap-2 lg:justify-end">
                    <div className="w-[180px] shrink-0">
                      <Input
                        type="text"
                        name="manuallyEnteredRate"
                        id="manuallyEnteredRate"
                        value={electricRateForm.manuallyEnteredRate}
                        onChange={handleManualRateChange}
                        placeholder="00.00"
                        inputMode="decimal"
                        disabled={isAccessRestricted}
                        className="h-12 w-full rounded-lg border border-[#D9D9D9] bg-white px-4 text-base text-[#2F343A] placeholder:text-dark-grey/50 focus:outline-none focus:ring-2 focus:ring-theme/30 disabled:cursor-not-allowed disabled:opacity-70 dark:border-[#FF8C65] dark:bg-[#16386F] dark:text-white dark:placeholder:text-white/55"
                      />
                    </div>
                    <span className="text-dark-grey dark:text-white text-lg leading-6 whitespace-nowrap">
                      ¢/kWh
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="basis-full lg:basis-7/12 xl:basis-8/12 bg-white dark:darkThemeGradiantBg shadow rounded-xl p-4 w-full">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SummaryCard
                bgColor="bg-[#E1F9FF]"
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="23" fill="#D5EEFF" stroke="#0057B8" strokeWidth="3" />
                  <path d="M36.988 14L23.936 34.352H31.52L27.012 50L40.064 29.648H32.48L36.988 14Z" fill="#0057B8" />
                </svg>}
                value={totalEnergyUsageDisplay}
                valueColor="text-[#0057B8]"
                label="Zone Total Energy Usage"
                subLabel={getLastYearSubLabel(totalEnergyUsageKWh, lastYearTotalEnergyKWh, "energy-total-usage-summary-card")}
                data-testid="energy-total-usage-summary-card"
              />
              <SummaryCard
                bgColor="bg-[#FFE4D1]"
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="23" fill="#FFF1E6" stroke="#D5671F" strokeWidth="3" />
                  <path d="M18 26C19.7 24.5 21.9 24.5 23.6 26C25.3 27.5 27.5 27.5 29.2 26" stroke="#D5671F" strokeWidth="3" strokeLinecap="round" />
                  <path d="M39.988 14L26.936 34.352H34.52L30.012 50L43.064 29.648H35.48L39.988 14Z" fill="#D5671F" />
                </svg>}
                value={avgPerUnitEnergyDisplay}
                valueColor="text-[#D5671F]"
                label={`Avg. per unit ${energyUnitLabel}`}
                subLabel={getLastYearSubLabel(avgPerUnitEnergyKWh, lastYearAvgPerUnitEnergyKWh, "energy-avg-per-unit-summary-card")}
                data-testid="energy-avg-per-unit-summary-card"
              />
            </div>
            <div className="flex justify-between items-center max-[430px]:flex-col max-[430px]:items-start mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-[#000000DE] dark:text-white" data-testid="energy-usage-over-time-title">
                Energy Usage Over Time
              </h2>
              <Button
                type="button"
                onClick={handleDownloadEnergyUsageOverTimeCsv}
                disabled={!chartData.length}
                aria-label="Download Energy Usage Over Time chart data as CSV"
                title="Download CSV"
                className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
                  chartData.length
                    ? "border-[#E1E5EA] text-theme hover:bg-[#FFF4EF] dark:border-darkThemeBorder dark:text-[#FF8C65]"
                    : "cursor-not-allowed border-[#E1E5EA] text-[#BDBDBD] dark:border-darkThemeBorder dark:text-white/30"
                }`}
                data-testid="energy-usage-over-time-download-button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M11.6667 1.66699H5.83333C4.91286 1.66699 4.16667 2.41318 4.16667 3.33366V16.667C4.16667 17.5875 4.91286 18.3337 5.83333 18.3337H14.1667C15.0871 18.3337 15.8333 17.5875 15.8333 16.667V5.83366L11.6667 1.66699Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.6667 1.66699V5.83366H15.8333"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 8.33301V13.333"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M8.125 11.458L10 13.333L11.875 11.458"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.5 15H12.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </Button>
            </div>
            {compareLastYear && energyUsage?.data?.lastYear && !hasLastYearEnergyData ? (
              <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="energy-last-year-empty-state">
                No data available for the same period last year
              </p>
            ) : null}
            <div className="grow h-full flex items-center justify-center min-h-[360px]">
              {chartData.length > 0 ? (
                <HistoricalRuntime
                  data={chartData}
                  lastYearLabel={hasLastYearEnergyData ? "Same time last year" : null}
                  energyUnit={energyUnit}
                  energyUnitLabel={energyUnitLabel}
                  valueLabel="Energy Usage"
                  axisLabel={`Energy Usage (${energyUnitLabel})`}
                  legendLabel={`Energy Usage (${energyUnitLabel})`}
                  secondaryAxisLabel={
                    hasConfiguredElectricRate ? "Energy Cost ($)" : null
                  }
                  secondaryValuePrefix={hasConfiguredElectricRate ? "$" : ""}
                  data-testid="energy-usage-over-time-chart"
                />
              ) : (
                <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="energy-usage-over-time-empty-state">
                  No data available
                </p>
              )}
            </div>
            <div className="border-t dark:border-t-darkThemeBorder pt-4">
              <div className="mb-4 flex justify-between items-center max-[430px]:flex-col max-[430px]:items-start">
                <h2 className="text-lg sm:text-xl font-semibold text-[#000000DE] dark:text-white" data-testid="heating-cooling-usage-title">
                  Heating/Cooling Energy Usage
                </h2>
                <Button
                  type="button"
                  onClick={handleDownloadHeatingCoolingEnergyUsageCsv}
                  disabled={!hasHeatingCoolingData}
                  aria-label="Download Heating/Cooling Energy Usage chart data as CSV"
                  title="Download CSV"
                  className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
                    hasHeatingCoolingData
                      ? "border-[#E1E5EA] text-theme hover:bg-[#FFF4EF] dark:border-darkThemeBorder dark:text-[#FF8C65]"
                      : "cursor-not-allowed border-[#E1E5EA] text-[#BDBDBD] dark:border-darkThemeBorder dark:text-white/30"
                  }`}
                  data-testid="heating-cooling-usage-download-button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M11.6667 1.66699H5.83333C4.91286 1.66699 4.16667 2.41318 4.16667 3.33366V16.667C4.16667 17.5875 4.91286 18.3337 5.83333 18.3337H14.1667C15.0871 18.3337 15.8333 17.5875 15.8333 16.667V5.83366L11.6667 1.66699Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.6667 1.66699V5.83366H15.8333"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 8.33301V13.333"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8.125 11.458L10 13.333L11.875 11.458"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.5 15H12.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </Button>
              </div>
              <div className="min-h-[240px] flex items-center justify-center">
                {hasHeatingCoolingData ? (
                  <RuntimeBarChart
                    data={runtimeChartData}
                    energyUnit={energyUnit}
                    energyUnitLabel={energyUnitLabel}
                    secondaryValuePrefix={hasConfiguredElectricRate ? "$" : ""}
                    showSecondaryValue={hasConfiguredElectricRate}
                    showLastYear={hasLastYearEnergyData}
                    data-testid="heating-cooling-usage-chart"
                  />
                ) : (
                  <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="heating-cooling-usage-empty-state">
                    No data available
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="basis-full lg:basis-5/12 xl:basis-4/12 self-start bg-white dark:darkThemeGradiantBg shadow rounded-xl w-full">
          <div className="flex flex-col gap-0">
            <h2 className="text-lg font-semibold p-4  border-b dark:border-b-darkThemeBorder dark:text-white" data-testid="top-users-title">
              Top Users
            </h2>
            <div
              className={`pt-0 flex flex-col ${
                topUnitsByRange.length > 0 ? "justify-start" : "justify-center"
              } items-center`}
            >
              {topUnitsByRange.length > 0 ? (
                <>
                  <div className="w-full">
                    {topUnitsByRange.map((unit, index) => renderTopUserRow(unit, index, "top-users"))}
                  </div>
                </>
              ) : (
                <div className="w-full px-4 py-10" data-testid="top-users-empty-state">
                  <p className="text-center text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]">
                    No data available for top users
                  </p>
                </div>
              )}
            </div>
            {compareLastYear ? (
              <div className="w-full border-t dark:border-t-darkThemeBorder" data-testid="top-users-last-year-section">
                <h3 className="text-base font-semibold p-4 border-b dark:border-b-darkThemeBorder dark:text-white" data-testid="top-users-last-year-title">
                  Top Users - Same Time Last Year
                </h3>
                {lastYearTopUnits.length > 0 ? (
                  <div className="w-full">
                    {lastYearTopUnits.map((unit, index) => renderTopUserRow(unit, index, "top-users-last-year"))}
                  </div>
                ) : (
                  <div className="w-full px-4 py-10" data-testid="top-users-last-year-empty-state">
                    <p className="text-center text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]">
                      No data available for top users in the same period last year
                    </p>
                  </div>
                )}
              </div>
            ) : null}
            {topUsersPageCount > 1 ? (
              <div className="w-full border-t dark:border-t-darkThemeBorder px-4 py-5">
                <Pagination
                  pageCount={topUsersPageCount}
                  currentPage={topUsersCurrentPage}
                  onPageChange={({ selected }) => setTopUsersCurrentPage(selected)}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="mt-6">
        <p className="text-xs leading-5 text-dark-grey dark:text-white/70">
          <span className="text-sm font-semibold text-[#00000099] dark:text-white/80">
            Power and Energy Data Disclaimer
          </span>{" "}
          Power and energy data displayed in this platform are estimates only,
          with an accuracy of approximately +/-5%. Such data is for
          informational purposes only and is not suitable for billing,
          settlement, or regulatory use.
        </p>
      </div>

      <ToastMessage
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />
    </>
  );
};

export default Energy;
